import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { bkAdmin, nok, errMsg, STATUS_LABEL } from "@/lib/booking";
import { ProfitCalc } from "@/pages/site/ProfitCalc";
import { Agreements, Codes, CustomersTab, Messages, SeoGoogle, PfAdmin } from "@/pages/site/AdminExtra";

const DAYS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
export const Num = ({ v, on, testid, className = "w-24" }) => <Input data-testid={testid} type="number" className={`h-8 ${className}`} value={v} onChange={(e) => on(Number(e.target.value))} />;
const sel = "h-8 rounded border bg-background text-xs";

const Bookings = ({ cleaners }) => {
  const [list, setList] = useState([]);
  const [kind, setKind] = useState("engang");
  const [status, setStatus] = useState("");
  const load = () => bkAdmin.bookings(status || undefined, kind || undefined).then(setList);
  useEffect(() => { load(); }, [kind, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = async (b, p) => {
    try { const r = await bkAdmin.updateBooking(b.id, p); toast.success(r.email_sent === false ? "Lagret (e-post ikke sendt)" : r.email_sent ? "Lagret – kunden er varslet" : "Lagret"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const refund = async (b) => {
    if (!window.confirm("Refundere hele beløpet?")) return;
    try { await bkAdmin.refund(b.id); toast.success("Refundert"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <div className="mb-3 flex flex-wrap gap-2">
        {[["engang", "Engangsbestillinger"], ["fast", "Besøk fra faste avtaler"], ["", "Alle"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} data-testid={`bookings-kind-${k || "alle"}`} onClick={() => setKind(k)}>{l}</Button>
        ))}
        <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="bookings-status-filter">
          <option value="">Alle statuser</option>{Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground border-b"><tr>{["Ref", "Dato/tid", "Tjeneste", "Kunde", "Adresse", "Beløp / DB", "Renholder", "Status", ""].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-${b.id}`} className="border-b last:border-0 align-top">
                <td className="p-2 font-mono text-xs">{b.ref}</td>
                <td className="p-2"><Input type="date" className="h-7 w-36 text-xs" defaultValue={b.date} onBlur={(e) => e.target.value !== b.date && upd(b, { date: e.target.value })} />
                  <Input type="time" className="h-7 w-24 mt-1 text-xs" defaultValue={b.start} onBlur={(e) => e.target.value !== b.start && upd(b, { start: e.target.value })} /></td>
                <td className="p-2">{b.service_name}<div className="text-xs text-muted-foreground">{b.sqm ? `${b.sqm} m²` : `${b.quantity} vinduer`}{b.pets ? " · kjæledyr" : ""}{b.kids ? " · barn" : ""} · {b.products === "kundens" ? "kundens produkter" : "vi tar med"}</div>
                  {b.allergies && <div className="text-xs text-amber-700">Hensyn: {b.allergies}</div>}
                  {b.details && <div className="text-xs italic max-w-[220px]">{b.details}</div>}</td>
                <td className="p-2">{b.name}<div className="text-xs">{b.phone}<br />{b.email}</div></td>
                <td className="p-2">{b.address}, {b.postcode}</td>
                <td className="p-2 whitespace-nowrap">{nok(b.price?.total)}<div className="text-xs text-muted-foreground">{b.payment_method === "card" ? `kort: ${b.payment_status}` : b.payment_method === "invoice" ? "faktura" : "avtale"}</div>
                  <div className={`text-xs font-semibold ${b.profit?.unprofitable ? "text-red-600" : b.profit?.below_target ? "text-amber-600" : "text-green-700"}`}>
                    {(b.profit?.unprofitable || b.profit?.below_target) && <AlertTriangle className="inline h-3 w-3 mr-0.5" />}DB {nok(b.profit?.margin)} ({b.profit?.margin_pct}%)</div></td>
                <td className="p-2"><select className={sel} value={b.cleaner_id || ""} onChange={(e) => upd(b, { cleaner_id: e.target.value })}>{cleaners.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></td>
                <td className="p-2"><select data-testid={`admin-booking-status-${b.id}`} className={sel} value={b.status} onChange={(e) => upd(b, { status: e.target.value })}>
                  {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                <td className="p-2">{b.payment_status === "paid" && <Button size="sm" variant="outline" onClick={() => refund(b)}>Refunder</Button>}</td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Ingen oppdrag.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
};

const Prices = () => {
  const [d, setD] = useState(null);
  useEffect(() => { bkAdmin.services().then(setD); }, []);
  if (!d) return null;
  const setS = (i, k, v) => { const s = [...d.services]; s[i] = { ...s[i], [k]: v }; setD({ ...d, services: s }); };
  const setA = (i, k, v) => { const a = [...d.addons]; a[i] = { ...a[i], [k]: v }; setD({ ...d, addons: a }); };
  const saveS = async (s) => { await bkAdmin.updateService(s.slug, s); toast.success(`${s.name} lagret`); };
  const saveA = async (a) => { await bkAdmin.updateAddon(a.key, a); toast.success(`${a.name} lagret`); };
  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card divide-y">
        {d.services.map((s, i) => (
          <div key={s.slug} data-testid={`admin-service-${s.slug}`} className="flex flex-wrap items-end gap-3 p-3 text-xs">
            <div className="min-w-[200px] flex-1"><Input className="h-8 font-semibold" value={s.name} onChange={(e) => setS(i, "name", e.target.value)} />
              <Input className="h-8 mt-1" value={s.description} onChange={(e) => setS(i, "description", e.target.value)} /></div>
            {s.unit === "vindu" ? <label>Kr/vindu<Num v={s.unit_price} on={(v) => setS(i, "unit_price", v)} /></label>
              : <label>Kr/m²<Num testid={`price-sqm-${s.slug}`} v={s.price_sqm} on={(v) => setS(i, "price_sqm", v)} /></label>}
            <label>Minstepris<Num testid={`min-price-${s.slug}`} v={s.min_price} on={(v) => setS(i, "min_price", v)} /></label>
            {s.unit !== "vindu" && <label>m² per time<Num v={s.sqm_per_hour} on={(v) => setS(i, "sqm_per_hour", v)} /></label>}
            <label className="flex items-center gap-1"><input type="checkbox" checked={s.bookable} onChange={(e) => setS(i, "bookable", e.target.checked)} />Bestillbar på nett</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={s.active} onChange={(e) => setS(i, "active", e.target.checked)} />Vises</label>
            <Button size="sm" data-testid={`save-service-${s.slug}`} onClick={() => saveS(s)}>Lagre</Button>
          </div>
        ))}
      </div>
      <h3 className="font-heading font-bold">Tillegg <span className="text-xs font-normal text-muted-foreground">(tjenester: kommaseparerte koder, tomt = alle)</span></h3>
      <div className="rounded-xl border bg-card divide-y">
        {d.addons.map((a, i) => (
          <div key={a.key} className="flex flex-wrap items-end gap-3 p-3 text-xs">
            <Input className="h-8 flex-1 min-w-[180px]" value={a.name} onChange={(e) => setA(i, "name", e.target.value)} />
            <label>Pris<Num v={a.price} on={(v) => setA(i, "price", v)} /></label>
            <label>Minutter<Num v={a.minutes} on={(v) => setA(i, "minutes", v)} /></label>
            <label className="flex-1 min-w-[200px]">Gjelder tjenester<Input className="h-8" value={(a.services || []).join(", ")} onChange={(e) => setA(i, "services", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={a.active} onChange={(e) => setA(i, "active", e.target.checked)} />Aktiv</label>
            <Button size="sm" onClick={() => saveA(a)}>Lagre</Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Tjenestekoder: {d.services.map((s) => s.slug).join(", ")}</p>
    </div>
  );
};

const Capacity = ({ cleaners, reload }) => {
  const [s, setS] = useState(null);
  const [name, setName] = useState("");
  useEffect(() => { bkAdmin.settings().then((x) => setS({ ...x, postcodes: x.postcodes.join(", ") })); }, []);
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });
  const setRP = (t, k, v) => set("recurring_pricing", { ...s.recurring_pricing, [t]: { ...s.recurring_pricing[t], [k]: v } });
  const save = async () => { await bkAdmin.saveSettings({ ...s, postcodes: s.postcodes.split(",").map((x) => x.trim()).filter(Boolean) }); toast.success("Innstillinger lagret"); };
  const toggleDay = (c, i) => bkAdmin.updateCleaner(c.id, { ...c, work_days: c.work_days.includes(i) ? c.work_days.filter((x) => x !== i) : [...c.work_days, i] }).then(reload);
  const field = (k, label, type = "number") => (
    <label className="text-xs">{label}<Input data-testid={`setting-${k}`} type={type} className="h-8 mt-1" value={s[k]} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} /></label>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <h3 className="font-heading font-bold">Åpningstid, dekning, betaling og mva</h3>
        <div className="grid grid-cols-2 gap-3">
          {field("work_start", "Fra kl.", "time")}{field("work_end", "Til kl.", "time")}
          {field("travel_buffer_min", "Reisetid mellom jobber (min)")}{field("min_notice_hours", "Minste varsel (timer)")}
          {field("max_days_ahead", "Maks dager frem")}{field("vat_rate", "Mva-sats %")}
        </div>
        <label className="text-xs block">Postnummer vi dekker (prefiks, kommaseparert)<Input data-testid="setting-postcodes" className="h-8 mt-1" value={s.postcodes} onChange={(e) => set("postcodes", e.target.value)} /></label>
        <div className="flex flex-wrap gap-4 text-sm">
          {[["vat_registered", "Mva-registrert"], ["allow_card", "Kortbetaling"], ["allow_invoice", "Faktura etter jobb"], ["auto_confirm", "Bekreft bestillinger automatisk"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5"><input data-testid={`setting-${k}`} type="checkbox" checked={s[k]} onChange={(e) => set(k, e.target.checked)} />{l}</label>
          ))}
        </div>
        <h3 className="font-heading font-bold pt-2">Veiledende priser – faste avtaler (per besøk)</h3>
        {Object.entries(s.recurring_pricing).map(([t, v]) => (
          <div key={t} className="flex flex-wrap items-end gap-2 text-xs"><span className="w-16 font-semibold capitalize">{t}</span>
            <label>Kr/m²<Num v={v.price_sqm} on={(x) => setRP(t, "price_sqm", x)} className="w-20" /></label>
            <label>Min.<Num v={v.min_price} on={(x) => setRP(t, "min_price", x)} className="w-20" /></label>
            <label>m²/time<Num v={v.sqm_per_hour} on={(x) => setRP(t, "sqm_per_hour", x)} className="w-20" /></label></div>
        ))}
        <div className="flex flex-wrap gap-2 text-xs">{Object.entries(s.recurring_freq_factor).map(([f, v]) => (
          <label key={f}>Faktor {f}<Num v={v} on={(x) => set("recurring_freq_factor", { ...s.recurring_freq_factor, [f]: x })} className="w-20" /></label>))}</div>
        <p className="text-xs text-muted-foreground">Faktor 1 = ingen rabatt. Sjekk lønnsomhet før du setter under 1.</p>
        <Button data-testid="save-settings" onClick={save}>Lagre innstillinger</Button>
      </div>
      <div className="rounded-xl border bg-card p-4 h-fit">
        <h3 className="font-heading font-bold">Renholdere (kapasitet)</h3>
        <p className="text-xs text-muted-foreground">Hver aktive renholder kan ta én jobb om gangen. Flere renholdere = flere ledige tider.</p>
        <div className="mt-3 divide-y">
          {cleaners.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <span className="font-semibold flex-1">{c.name}</span>
              {DAYS.map((d, i) => <button key={d} onClick={() => toggleDay(c, i)} className={`rounded px-1.5 py-0.5 text-xs ${c.work_days.includes(i) ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{d}</button>)}
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={c.active} onChange={(e) => bkAdmin.updateCleaner(c.id, { ...c, active: e.target.checked }).then(reload)} />Aktiv</label>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input data-testid="new-cleaner-name" className="h-8" placeholder="Navn på renholder" value={name} onChange={(e) => setName(e.target.value)} />
          <Button size="sm" data-testid="add-cleaner" onClick={() => name && bkAdmin.addCleaner({ name, active: true, work_days: [0, 1, 2, 3, 4] }).then(() => { setName(""); reload(); })}>Legg til</Button>
        </div>
      </div>
    </div>
  );
};

const Banner = ({ o }) => o && (
  <div className="mb-4 flex flex-wrap gap-2 text-xs" data-testid="admin-status-banner">
    {o.stripe_test_mode && <span className="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-800">Stripe i TESTMODUS – ingen ekte betalinger</span>}
    <span className={`rounded-full px-3 py-1 font-semibold ${o.gmail_connected ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{o.gmail_connected ? "E-postvarsler aktive (Gmail)" : "Gmail ikke tilkoblet – ingen e-post sendes"}</span>
    {o.pending_approval > 0 && <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800">{o.pending_approval} venter på godkjenning</span>}
    {o.new_requests > 0 && <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800">{o.new_requests} nye forespørsler om fast avtale</span>}
    {o.unread_contacts > 0 && <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800">{o.unread_contacts} nye meldinger</span>}
  </div>
);

export default function BookingAdmin() {
  const [cleaners, setCleaners] = useState([]);
  const [o, setO] = useState(null);
  const reload = () => bkAdmin.cleaners().then(setCleaners);
  useEffect(() => {
    reload(); bkAdmin.overview().then(setO);
    document.title = "Admin – Ask-Stokke Renhold";
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex, nofollow"; document.head.appendChild(m);
    return () => m.remove();
  }, []);
  const tabs = [["bookings", "Engangsbestillinger"], ["agreements", "Faste avtaler"], ["customers", "Kunder"], ["prices", "Tjenester og priser"], ["codes", "Rabattkoder"],
    ["capacity", "Kapasitet og innstillinger"], ["profit", "Lønnsomhet"], ["messages", "E-post og meldinger"], ["seo", "SEO og Google"], ["pf", "Prospect Finder"]];
  return (
    <div className="min-h-screen bg-background p-4 sm:p-8" data-testid="booking-admin-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="font-heading text-3xl font-extrabold">Ask-Stokke – admin</h1>
        <Link to="/pro" className="ml-auto text-sm text-muted-foreground hover:text-primary">Internt system</Link>
        <Link to="/" className="text-sm text-muted-foreground hover:text-primary">Til nettsiden →</Link>
      </div>
      <Banner o={o} />
      <Tabs defaultValue="bookings">
        <TabsList className="flex-wrap h-auto">{tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        <TabsContent value="bookings" className="mt-4"><Bookings cleaners={cleaners} /></TabsContent>
        <TabsContent value="agreements" className="mt-4"><Agreements cleaners={cleaners} /></TabsContent>
        <TabsContent value="customers" className="mt-4"><CustomersTab /></TabsContent>
        <TabsContent value="prices" className="mt-4"><Prices /></TabsContent>
        <TabsContent value="codes" className="mt-4"><Codes /></TabsContent>
        <TabsContent value="capacity" className="mt-4"><Capacity cleaners={cleaners} reload={reload} /></TabsContent>
        <TabsContent value="profit" className="mt-4"><ProfitCalc /></TabsContent>
        <TabsContent value="messages" className="mt-4"><Messages /></TabsContent>
        <TabsContent value="seo" className="mt-4"><SeoGoogle /></TabsContent>
        <TabsContent value="pf" className="mt-4"><PfAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
